import { useEffect, useState } from 'react';

type VideoState = {
  playing: boolean;
  muted: boolean;
  ready: boolean;
  currentTime: number;
  duration: number;
};

export const useVideoState = (video: HTMLVideoElement | null) => {
  const [state, setState] = useState<VideoState>({
    playing: false,
    muted: false,
    ready: false,
    currentTime: 0,
    duration: 0,
  });

  useEffect(() => {
    if (!video) {
      return;
    }

    const updateTime = () => {
      setState((prev) => ({
        ...prev,
        currentTime: video.currentTime,
        duration: video.duration || 0,
      }));
    };

    const onPlay = () => setState((prev) => ({ ...prev, playing: true }));

    const onPause = () => setState((prev) => ({ ...prev, playing: false }));

    const onLoadedMetadata = () =>
      setState((prev) => ({
        ...prev,
        duration: video.duration,
      }));

    const onCanPlay = () => setState((prev) => ({ ...prev, ready: true }));

    const onWaiting = () => setState((prev) => ({ ...prev, ready: false }));

    setState({
      playing: !video.paused,
      muted: video.muted,
      ready: video.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA,
      currentTime: video.currentTime,
      duration: video.duration || 0,
    });

    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('timeupdate', updateTime);
    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('canplay', onCanPlay);
    video.addEventListener('waiting', onWaiting);

    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('timeupdate', updateTime);
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('canplay', onCanPlay);
      video.removeEventListener('waiting', onWaiting);
    };
  }, [video]);

  return state;
};
